import * as CookieConsent from 'vanilla-cookieconsent';
import 'vanilla-cookieconsent/dist/cookieconsent.css';

const GTM_ID = 'GTM-MPQKK7C';
const CONSENT_COOKIE_NAME = 'drive2_cookie_consent';
const ANALYTICS_COOKIE_NAMES = [
  /^_ga(?:_[A-Z0-9]+)?$/i,
  /^_gid$/i,
  /^_gat(?:_.+)?$/i,
  /^_gcl_.+$/i,
  /^__utm[a-z]+$/i,
  /^_clck$/i,
  /^_clsk$/i,
  /^CLID$/i,
  /^ANONCHK$/i,
  /^MR$/i,
  /^MUID$/i,
  /^SM$/i,
];

let initialized = false;
let gtmLoaded = false;
let lastPreferenceSignature = null;

const isProduction = () =>
  process.env.NODE_ENV === 'production' &&
  (window.location.hostname === 'drive2.city' || window.location.hostname.endsWith('.drive2.city'));

const hasAcceptedCategory = (category) => CookieConsent.acceptedCategory(category);

const deleteCookie = (name, domain) => {
  const domainAttribute = domain ? `; Domain=${domain}` : '';
  document.cookie = `${name}=; Path=/; Max-Age=0${domainAttribute}; SameSite=Lax`;
};

const clearRevokedAnalyticsCookies = () => {
  const names = document.cookie.split(/;\s*/).map((cookie) => cookie.split('=')[0]);
  const domains = ['', window.location.hostname, `.${window.location.hostname}`];

  if (window.location.hostname.endsWith('.drive2.city')) {
    domains.push('.drive2.city');
  }

  names.filter((name) => ANALYTICS_COOKIE_NAMES.some((pattern) => pattern.test(name)))
    .forEach((name) => domains.forEach((domain) => deleteCookie(name, domain)));
};

const loadGtmOnce = () => {
  if (gtmLoaded || document.querySelector(`script[data-drive2-gtm="${GTM_ID}"]`)) {
    gtmLoaded = true;
    return;
  }

  gtmLoaded = true;
  const script = document.createElement('script');
  script.async = true;
  script.src = `https://www.googletagmanager.com/gtm.js?id=${GTM_ID}`;
  script.setAttribute('data-drive2-gtm', GTM_ID);
  document.head.appendChild(script);
};

const setGoogleConsentDefaults = () => {
  window.dataLayer = window.dataLayer || [];
  window.gtag = window.gtag || function gtag() {
    window.dataLayer.push(arguments);
  };
  window.gtag('consent', 'default', {
    analytics_storage: 'denied',
    ad_storage: 'denied',
    ad_user_data: 'denied',
    ad_personalization: 'denied',
    functionality_storage: 'granted',
    security_storage: 'granted',
    wait_for_update: 500,
  });
};

const applyConsent = () => {
  const analytics = hasAcceptedCategory('analytics');
  const externalMedia = hasAcceptedCategory('external_media');
  const signature = `${analytics}:${externalMedia}`;

  if (signature === lastPreferenceSignature) {
    return;
  }

  lastPreferenceSignature = signature;
  window.gtag('consent', 'update', {
    analytics_storage: analytics ? 'granted' : 'denied',
    ad_storage: 'denied',
    ad_user_data: 'denied',
    ad_personalization: 'denied',
    functionality_storage: 'granted',
    security_storage: 'granted',
  });
  window.dataLayer.push({
    event: 'drive2_consent_update',
    drive2_analytics_consent: analytics,
    drive2_external_media_consent: externalMedia,
  });

  if (!analytics) {
    clearRevokedAnalyticsCookies();
  }

  loadGtmOnce();
};

const consentConfig = () => ({
  revision: 1,
  cookie: {
    name: CONSENT_COOKIE_NAME,
    domain: isProduction() ? '.drive2.city' : '',
    path: '/',
    sameSite: 'Lax',
    expiresAfterDays: 180,
    secure: isProduction() && window.location.protocol === 'https:',
  },
  guiOptions: {
    consentModal: { layout: 'box wide', position: 'bottom center', equalWeightButtons: true },
    preferencesModal: { layout: 'box', position: 'right', equalWeightButtons: true },
  },
  categories: {
    necessary: {
      enabled: true,
      readOnly: true,
    },
    analytics: {
      enabled: false,
    },
    external_media: {
      enabled: false,
    },
  },
  language: {
    default: 'en',
    translations: {
      en: {
        consentModal: {
          title: 'Cookie preferences',
          description: 'We use essential cookies to run DRIVE2.CITY. With your permission, we also use analytics and external media cookies.',
          acceptAllBtn: 'Accept all',
          acceptNecessaryBtn: 'Reject all',
          showPreferencesBtn: 'Manage preferences',
        },
        preferencesModal: {
          title: 'Cookie preferences',
          acceptAllBtn: 'Accept all',
          acceptNecessaryBtn: 'Reject all',
          savePreferencesBtn: 'Save preferences',
          closeIconLabel: 'Close',
          sections: [
            {
              title: 'Necessary',
              description: 'Required for authentication, Firebase sessions, maps, routing, security, checkout, Stripe and PayPal.',
              linkedCategory: 'necessary',
            },
            {
              title: 'Analytics',
              description: 'Helps us understand usage through Google Analytics 4 and Microsoft Clarity only.',
              linkedCategory: 'analytics',
            },
            {
              title: 'External media',
              description: 'Enables third-party embeds and widgets when they are present.',
              linkedCategory: 'external_media',
            },
          ],
        },
      },
    },
  },
  onConsent: applyConsent,
  onChange: applyConsent,
});

export const initializeCookieConsent = () => {
  if (typeof window === 'undefined' || initialized) {
    return;
  }

  initialized = true;
  setGoogleConsentDefaults();
  CookieConsent.run(consentConfig());
};

export const openCookieSettings = () => {
  if (typeof window !== 'undefined' && initialized) {
    CookieConsent.showPreferences();
  }
};
